import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Floor, Order, OrderProduct, Table } from '../interfaces';
import { StyleSheet } from 'react-native';
import ErrorNotification from '../components/notifications/ErrorNotification';
import Spinner from '../components/Spinner';
import ConfirmOrderAction from '../components/popups/ConfirmOrderAction';
import './Tables.css';
import ReceiptPopup from '../components/ReceiptPopup';
import { getRef, putRef } from '../services/OfflineDb';
import CashPaymentPopup from '../components/CashPaymentPopup';
import CardPaymentPopup from '../components/CardPaymentPopup';
import { useAuth } from '../AuthContext';
import { Reservation, ReservationClash } from '../interfaces';
import useReservations, {
    TurnStatus,
    lateGraceMinutes,
    lateStatus,
    minutesOfTime,
    plannedTurnMinutes,
    toDateKey,
    turnStatus,
    waitStatus,
} from '../hooks/useReservations';
import ReservationsRail, { RailSide } from '../components/reservations/ReservationsRail';
import ServiceTimeline, { Occupancy } from '../components/reservations/ServiceTimeline';
import ReservationSheet from '../components/reservations/ReservationSheet';
import WaitlistSheet from '../components/reservations/WaitlistSheet';
import '../components/reservations/Reservations.css';
import { setSeatingHandoff } from '../services/reservationSeating';

type FloorView = 'map' | 'states' | 'sections' | 'timeline';

/** What the timeline hands the booking sheet when a gap in it is tapped. */
type SheetPrefill = { time?: string; tableIds?: string[] } | null;

type ServiceStateKey =
    | 'available' | 'seated' | 'ordered' | 'fired' | 'served' | 'check' | 'paid' | 'dirty';

interface ServiceStateDef { label: string; accent: string; tint: string; hint: string; }

// Lifecycle ordering used by the legend and the inline state picker.
const SERVICE_STATE_ORDER: ServiceStateKey[] = [
    'available', 'seated', 'ordered', 'fired', 'served', 'check', 'paid', 'dirty',
];

const SERVICE_STATES: Record<ServiceStateKey, ServiceStateDef> = {
    available: { label: 'Available', accent: 'var(--color-success-600)', tint: 'color-mix(in srgb, var(--color-success-600) 12%, transparent)', hint: 'Open for seating' },
    seated: { label: 'Seated', accent: '#2563eb', tint: 'color-mix(in srgb, #2563eb 12%, transparent)', hint: 'Greet & take order' },
    ordered: { label: 'Ordered', accent: 'var(--color-primary)', tint: 'color-mix(in srgb, var(--color-primary) 12%, transparent)', hint: 'Sent to kitchen' },
    fired: { label: 'Cooking', accent: '#d97706', tint: 'color-mix(in srgb, #d97706 14%, transparent)', hint: 'On the line' },
    served: { label: 'Served', accent: '#0891b2', tint: 'color-mix(in srgb, #0891b2 13%, transparent)', hint: 'Dining — check back' },
    check: { label: 'Check dropped', accent: '#7c3aed', tint: 'color-mix(in srgb, #7c3aed 13%, transparent)', hint: 'Awaiting payment' },
    paid: { label: 'Paid', accent: 'var(--color-success-700)', tint: 'color-mix(in srgb, var(--color-success-700) 13%, transparent)', hint: 'Awaiting departure' },
    dirty: { label: 'Needs bussing', accent: 'var(--color-danger-600)', tint: 'color-mix(in srgb, var(--color-danger-600) 12%, transparent)', hint: 'Clear & reset' },
};

// Which states make sense while an order is still open vs. after it closes.
const ACTIVE_STATES: ServiceStateKey[] = ['seated', 'ordered', 'fired', 'served', 'check'];
const CLOSED_STATES: ServiceStateKey[] = ['paid', 'dirty'];

// Stable color per server name (sections view) — hashed into a fixed palette.
/** Grace between seating a booking and expecting its table to have an order. */
const SEATED_GRACE_MS = 2 * 60 * 1000;

/**
 * How far a saved floor plan may be shrunk to fit the space it is given. Nodes
 * already refuse to go below 66px, so squeezing the gaps any further only pushes
 * tables on top of each other — on a 340px-wide board eight pairs overlapped and
 * there was no telling which one a tap would hit. Past this the map keeps its
 * size and the board scrolls instead, which a finger does natively.
 */
const MIN_FLOOR_SCALE = 0.55;
/** A board shorter than this is not worth drawing a room on. */
const MIN_BOARD_HEIGHT = 420;

const SERVER_PALETTE = ['#2563eb', '#0891b2', '#7c3aed', '#d97706', '#059669', '#db2777', '#0d9488', '#c026d3'];
const serverColor = (name: string): string => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    return SERVER_PALETTE[h % SERVER_PALETTE.length];
};
const initialsOf = (name: string): string =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '—';

const loadSelectedFloorId = (setSelectedFloorId: (id: string) => void, fetchFloor: (id: string) => void) => {
    const savedFloor = localStorage.getItem('selectedFloor');
    if (savedFloor) {
        const parsedFloor = JSON.parse(savedFloor);
        setSelectedFloorId(parsedFloor.value);
        fetchFloor(parsedFloor.value);
    }
};

const Tables: React.FC = () => {
    const { hasPerm, lock } = useAuth();
    const can = (key: string) => !hasPerm || hasPerm(key);
    const [searchParams] = useSearchParams();
    const isZReportMode = searchParams.get('mode') === 'zreport';
    const openOrdersCount = parseInt(searchParams.get('openOrders') || '0');

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const navigate = useNavigate();
    const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
    const [floor, setFloor] = useState<Floor | null>(null);
    /** The space the board has to draw a floor in, not the board's own size. */
    const [boardSize, setboardSize] = useState({ width: 0, height: 0 });
    const divRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const [tableToMergeFrom, setTableToMergeFrom] = useState<string | null>(null);
    const [selectedTablesToMerge, setSelectedTablesToMerge] = useState<string[]>([]);
    const [tableToTransferProductsFrom, setTableToTransferProductsFrom] = useState<string | null>(null);
    const [tableToTransferProductsTo, setTableToTransferProductsTo] = useState<string | null>(null);
    const [productsToTransfer, setProductsToTransfer] = useState<string[]>([]);
    const [combinationsToTransfer, setCombinationsToTransfer] = useState<string[]>([]);
    // const [orderToMerge, setOrderToMerge] = useState<Order | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [titleError, setTitleError] = useState('');
    const [contentError, setContentError] = useState('');
    const [showError, setShowError] = useState(false);
    const [activeOrder, setActiveOrder] = useState<Order | undefined>(undefined);
    const [orderProducts, setOrderProducts] = useState<OrderProduct[]>([]);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const [isCashPaymentOpen, setIsCashPaymentOpen] = useState(false);
    const [isCardPaymentOpen, setIsCardPaymentOpen] = useState(false);
    const [remainingOpenTables, setRemainingOpenTables] = useState(openOrdersCount);

    // ── Floor-plan view (map / states / sections) — light-first, in the shell ─
    const [viewMode, setViewMode] = useState<FloorView>(() => {
        const saved = localStorage.getItem('tvFloorView');
        if (saved === 'map' || saved === 'states' || saved === 'sections' || saved === 'timeline') return saved;
        if (saved === 'grid') return 'states'; // migrate old "grid" view
        return 'map';
    });
    const [floors, setFloors] = useState<Floor[]>([]);
    const [now, setNow] = useState<number>(Date.now());

    // Service-state markers are a per-device operational layer: the backend only
    // tells us free/occupied + elapsed, so staff refine the dining lifecycle here.
    const [svcStates, setSvcStates] = useState<Record<string, ServiceStateKey>>(
        () => {
            try { return JSON.parse(localStorage.getItem('tvSvcStates') || '{}'); }
            catch { return {}; }
        }
    );
    const [stateMenuFor, setStateMenuFor] = useState<string | null>(null);
    // Viewport rect of the badge the menu hangs off. The menu is portalled to <body> and
    // positioned against this, so it escapes .tv-board-wrap's overflow and the screen edge.
    const [stateMenuAnchor, setStateMenuAnchor] = useState<DOMRect | null>(null);

    const closeStateMenu = () => {
        setStateMenuFor(null);
        setStateMenuAnchor(null);
    };

    const toggleStateMenu = (tableId: string, trigger: HTMLElement) => {
        if (stateMenuFor === tableId) {
            closeStateMenu();
            return;
        }
        setStateMenuAnchor(trigger.getBoundingClientRect());
        setStateMenuFor(tableId);
    };
    const [stateFilter, setStateFilter] = useState<ServiceStateKey | 'all'>('all');
    const [sectionScope, setSectionScope] = useState<'all' | 'mine' | string>('all');
    const [currentUserName, setCurrentUserName] = useState<string>('');

    // ── Reservations rail — a side panel that works across all three views ───
    const canSeeReservations = can('pos-app.reservation.view');
    const [railOpen, setRailOpen] = useState<boolean>(
        () => localStorage.getItem('tvResvOpen') === 'true',
    );
    const [railSide, setRailSide] = useState<RailSide>(
        () => (localStorage.getItem('tvResvSide') === 'right' ? 'right' : 'left'),
    );
    const [sheetFor, setSheetFor] = useState<Reservation | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetPrefill, setSheetPrefill] = useState<SheetPrefill>(null);
    const [waitSheetOpen, setWaitSheetOpen] = useState(false);
    /** The queue entry being corrected, or null when adding a new walk-in. */
    const [waitSheetFor, setWaitSheetFor] = useState<Reservation | null>(null);
    /** Set when "Seat" was pressed on a booking with no table yet. */
    const [seatAfterAssign, setSeatAfterAssign] = useState<string | null>(null);

    const resv = useReservations(selectedFloorId);

    const toggleRail = () => {
        setRailOpen((open) => {
            const next = !open;
            localStorage.setItem('tvResvOpen', String(next));
            if (!next) resv.cancelAssign();
            return next;
        });
    };

    const changeRailSide = (side: RailSide) => {
        setRailSide(side);
        localStorage.setItem('tvResvSide', side);
    };

    const setView = (v: FloorView) => {
        setViewMode(v);
        closeStateMenu();
        localStorage.setItem('tvFloorView', v);
    };

    const setTableServiceState = (tableId: string, key: ServiceStateKey) => {
        setSvcStates((prev) => {
            const next = { ...prev };
            if (key === 'available') delete next[tableId];
            else next[tableId] = key;
            localStorage.setItem('tvSvcStates', JSON.stringify(next));
            return next;
        });
        closeStateMenu();
    };

    // The menu is anchored to a rect captured at open time, so anything that moves the badge
    // — scrolling the board, resizing, rotating a tablet — would leave it stranded. Closing is
    // the honest response: the badge it belongs to may no longer be where the user is looking.
    useEffect(() => {
        if (!stateMenuFor) return;
        const close = () => closeStateMenu();
        window.addEventListener('resize', close);
        // Capture phase, because the scroll happens on .tv-board-wrap rather than the window.
        window.addEventListener('scroll', close, true);
        return () => {
            window.removeEventListener('resize', close);
            window.removeEventListener('scroll', close, true);
        };
    }, [stateMenuFor]);

    // Resolve the current signed-in user once (powers "My tables" in Sections).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/User/get-by-email`, { credentials: 'include' });
                if (!res.ok) return;
                const u = await res.json();
                const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.userName || u.email || '';
                if (!cancelled) setCurrentUserName(name);
            } catch { /* non-fatal — "My tables" just won't match */ }
        })();
        return () => { cancelled = true; };
    }, []);
    const switchFloor = (f: Floor) => {
        if (f.id === selectedFloorId) return;
        localStorage.setItem('selectedFloor', JSON.stringify({ value: f.id, label: f.floorName }));
        setSelectedFloorId(f.id);
    };

    // Tick so the live "elapsed" timers on occupied tables stay current.
    useEffect(() => {
        const tick = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(tick);
    }, []);

    /**
     * Closes bookings whose party has clearly gone.
     *
     * A booking is only "seated" while someone is sitting there, but nothing in
     * the payment flow says so: a table can be paid and cleared from cash, card,
     * split, room charge or mark-as-paid, and hooking all of them would leave the
     * booking open the day one of them changes. So the board reconciles instead —
     * a seated booking whose table no longer has an open order is finished.
     *
     * Held back for a couple of minutes because seating hands off to the order
     * screen before the order exists, and that gap must not read as "left".
     */
    const completedBookings = useRef<Set<string>>(new Set());
    const { isToday: resvIsToday, onDate: bookingsOnDate, setStatus: setBookingStatus } = resv;
    useEffect(() => {
        const onFloor = floor?.tables || [];
        if (!resvIsToday || onFloor.length === 0) return;
        bookingsOnDate.forEach((r) => {
            if (r.status !== 'seated' || completedBookings.current.has(r.id)) return;
            const seatedAt = r.seatedAt ? new Date(r.seatedAt).getTime() : 0;
            if (!seatedAt || Date.now() - seatedAt < SEATED_GRACE_MS) return;
            // Only judge tables this floor actually loaded; a booking on another
            // floor tells us nothing from here.
            const known = r.tableIds.filter(id => onFloor.some(t => t.id === id));
            if (known.length === 0) return;
            if (known.some(id => onFloor.find(t => t.id === id)?.hasActiveOrder)) return;
            completedBookings.current.add(r.id);
            setBookingStatus(r, 'completed');
        });
    }, [floor, resvIsToday, bookingsOnDate, setBookingStatus]);

    // Floor list powers the command-dock floor pills (with live busy counts).
    useEffect(() => {
        const loadFloors = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Floor/active`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setFloors(data);
                    putRef('activeFloors', data).catch(() => {});
                }
            } catch {
                const cached = await getRef('activeFloors');
                if (cached) setFloors(cached);
            }
        };
        loadFloors();
    }, [floor]);

    // Table positions are scaled off the space the board is given, so it has to
    // be re-measured whenever that changes — entering the map, the window
    // resizing, or the reservations rail taking width away from it. Observing the
    // element covers all of those without listing the causes.
    //
    // It measures the scroll container, not the board inside it: the board may now
    // be wider than the space available, and measuring something whose size is
    // derived from this measurement would never settle.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const measure = () => {
            if (el.clientWidth && el.clientHeight) {
                setboardSize({ width: el.clientWidth, height: el.clientHeight });
            }
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [viewMode, floor]);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const cancelMerge = () => {
        localStorage.removeItem('orderToMerge');
        localStorage.removeItem('tableToMergeFrom');
        navigate("/tables/" + tableToMergeFrom);
    }

    const handlePaymentComplete = () => {
        setIsCashPaymentOpen(false);
        setIsCardPaymentOpen(false);
        setActiveOrder(undefined);
        setOrderProducts([]);

        if (selectedFloorId) {
            fetchFloor(selectedFloorId);
        }

        const newCount = remainingOpenTables - 1;
        setRemainingOpenTables(newCount);

        if (newCount <= 0) {
            navigate('/close-shift');
        }
    };

    const fetchOrderForTable = async (tableId: string) => {
        try {
            setShowSpinner(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/get-active-order-for-table/${tableId}`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setActiveOrder(data);

                // Map products with the structure that ReceiptPopup expects
                if (data.products) {
                    const productsWithDetails = data.products.map((p: any) => ({
                        ...p,
                        product: {
                            price: p.price,
                            plu: p.plu || p.productId,
                            taxRate: p.taxRate || 0,
                            taxType: p.taxType || 'percentage'
                        },
                        addons: p.addons?.map((addon: any) => ({
                            ...addon,
                            addon: {
                                id: addon.addonId,
                                name: addon.addonName,
                                priceWithoutTax: addon.price
                            }
                        })) || [],
                        variants: p.variants || []
                    }));
                    setOrderProducts(productsWithDetails);
                }

                setIsReceiptOpen(true);
            }
        } catch (error) {
            console.error('Failed to fetch order:', error);
            setTitleError("Error!");
            setContentError("Failed to load order. Please try again.");
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
        } finally {
            setShowSpinner(false);
        }
    };

    // ── Reservation actions ──────────────────────────────────────────────────

    /** Tables staged for the booking being assigned (draft until confirmed). */
    const [assignPicks, setAssignPicks] = useState<string[]>([]);
    /** Clashes the server refused the assignment over, which the board could not see. */
    const [refusedClashes, setRefusedClashes] = useState<ReservationClash[]>([]);

    useEffect(() => {
        setAssignPicks(resv.assigning?.tableIds || []);
    }, [resv.assigning]);

    // Any change to the staging makes an earlier refusal stale.
    useEffect(() => { setRefusedClashes([]); }, [assignPicks]);

    const toggleTableForAssign = (table: Table) => {
        setAssignPicks((prev) =>
            prev.includes(table.id) ? prev.filter((id) => id !== table.id) : [...prev, table.id],
        );
    };

    // No view switch: all three lenses render tappable tables, so assigning works
    // wherever the user already is rather than yanking them back to the map.
    const beginAssign = (r: Reservation) => resv.startAssign(r);

    const confirmAssign = async () => {
        const booking = resv.assigning;
        if (!booking) return;
        // Seating two parties on one table is a decision a host is allowed to make;
        // making it by accident is not. The clash is named in the bar and the button
        // reads "Assign anyway", so pressing it is the acknowledgement.
        const result = await resv.assignTables(
            assignPicks, selectedFloorId || undefined, assignClashes.length > 0,
        );
        if (!result.ok) {
            setRefusedClashes(result.clashes);
            return;
        }
        // "Seat" on an unassigned booking parks the intent until it has a table.
        if (seatAfterAssign === booking.id && assignPicks.length > 0) {
            setSeatAfterAssign(null);
            startSeating({ ...booking, tableIds: assignPicks }, assignPicks[0]);
        }
    };

    const abortAssign = () => {
        setSeatAfterAssign(null);
        resv.cancelAssign();
    };

    /** Hands off to the order screen, which opens the table-setup dialog. */
    const startSeating = (booking: Reservation, tableId: string) => {
        setSeatingHandoff(booking, tableId);
        navigate(`/tables/${tableId}`);
    };

    const handleSeat = (booking: Reservation) => {
        if (!can('pos-app.order.create')) {
            setTitleError('Access Denied');
            setContentError("You don't have permission to open a new table.");
            setShowError(true);
            setTimeout(() => setShowError(false), 3000);
            return;
        }
        if (booking.tableIds.length === 0) {
            setSeatAfterAssign(booking.id);
            beginAssign(booking);
            return;
        }
        startSeating(booking, booking.tableIds[0]);
    };

    /**
     * `prefill` carries what the timeline knew when it was tapped — the time under
     * the finger and the lane it landed in — so booking a gap starts from the gap
     * rather than from an empty form the host has to re-describe.
     */
    const openSheet = (booking: Reservation | null, prefill: SheetPrefill = null) => {
        setSheetFor(booking);
        setSheetPrefill(prefill);
        setSheetOpen(true);
    };

    const handleTableClick = (event: any, table: Table) => {
        event.stopPropagation();

        // Assigning a booking takes priority: the whole board becomes a picker.
        if (resv.assigning) {
            if (!table.isActive) return;
            toggleTableForAssign(table);
            return;
        }

        if (!table.isActive || isTableDisabled(table)) return;

        if (!isTableDisabled(table)) {
            if (tableToMergeFrom != null) {
                setSelectedTablesToMerge(prevSelectedTables => {
                    if (prevSelectedTables.includes(table.id)) {
                        return prevSelectedTables.filter(id => id !== table.id);
                    } else {
                        return [...prevSelectedTables, table.id];
                    }
                });
            }
            else if (tableToTransferProductsFrom) {
                setTableToTransferProductsTo(table.id);
            }
            else if (isZReportMode) {
                // Z-Report mode: open receipt sidebar instead of navigating
                if (table.hasActiveOrder) {
                    fetchOrderForTable(table.id);
                }
            }
            else {
                // Opening an empty table starts a new order, so it requires the create permission.
                // Tables that already have an active order can still be opened (to view/pay/etc.).
                if (!table.hasActiveOrder && !can('pos-app.order.create')) {
                    setTitleError("Access Denied");
                    setContentError("You don't have permission to open a new table.");
                    setShowError(true);
                    setTimeout(() => setShowError(false), 3000);
                    return;
                }
                navigate("/tables/" + table.id);
            }
        }
    };


    const handleConfirmMerge = async () => {
        if (!can('pos-app.order.merge')) return;
        try {
            setShowSpinner(true);
            setsShowConfirmMerge(false);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/merge-tables`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tablesToMerge: selectedTablesToMerge
                }),
                credentials: 'include',
            });
            if (response) {
                // Merge seats from all tables into the main table
                if (tableToMergeFrom && selectedTablesToMerge.length > 0) {
                    try {
                        const mainSeats = JSON.parse(localStorage.getItem(`seats_${tableToMergeFrom}`) || '[]');
                        for (const mergedTableId of selectedTablesToMerge) {
                            if (mergedTableId === tableToMergeFrom) continue;
                            const mergedSeats = JSON.parse(localStorage.getItem(`seats_${mergedTableId}`) || '[]');
                            mainSeats.push(...mergedSeats);
                            localStorage.removeItem(`seats_${mergedTableId}`);
                        }
                        if (mainSeats.length > 0) {
                            localStorage.setItem(`seats_${tableToMergeFrom}`, JSON.stringify(mainSeats));
                        }
                    } catch (e) {
                        console.error('Error merging seats:', e);
                    }
                }

                localStorage.removeItem('orderToMerge');
                localStorage.removeItem('tableToMergeFrom');
                setShowSpinner(false);
                navigate("/tables/" + tableToMergeFrom);
            }
        } catch (error) {
            setShowSpinner(false);
            setTitleError("Error!");
            setContentError("There was a problem while merging the tables. Please try again.");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
    }

    useEffect(() => {
        const savedTable = localStorage.getItem('tableToMergeFrom');
        const savedOrder = localStorage.getItem('orderToMerge');

        if (savedTable) {
            const tab = JSON.parse(savedTable);
            setTableToMergeFrom(tab);

            setSelectedTablesToMerge((prevTables) => {
                if (!prevTables.includes(tab)) {
                    return [...prevTables, tab];
                }
                return prevTables;
            });
        }

        if (savedOrder) {
            const ord = JSON.parse(savedOrder);
            if (ord.mergedTablesIds) {
                setSelectedTablesToMerge((prevTables) => {
                    const updatedTables = new Set([...prevTables, ...ord.mergedTablesIds]);
                    return Array.from(updatedTables);
                });
            }
        }

        const savedTableToTransferFrom = localStorage.getItem('transferFromTable');
        const productToTransfer = localStorage.getItem('productsToTransfer');
        const combinationsToTransfer = localStorage.getItem('combinationsToTransfer');

        if (savedTableToTransferFrom) {
            const savedTable = JSON.parse(savedTableToTransferFrom);
            setTableToTransferProductsFrom(savedTable);
        }

        if (productToTransfer) {
            const prods = JSON.parse(productToTransfer);
            setProductsToTransfer(prods);
        }

        if (combinationsToTransfer) {
            const comb = JSON.parse(combinationsToTransfer);
            setCombinationsToTransfer(comb);
        }
    }, []);


    const cancelTransfer = () => {
        localStorage.removeItem('transferFromTable');
        localStorage.removeItem('productsToTransfer');
        localStorage.removeItem('combinationsToTransfer');
        navigate("/tables/" + tableToTransferProductsFrom);
    }

    useEffect(() => {
        const handleResize = () => {
            console.log("onresize: innerWidth: " + window.innerWidth + "innerHeight: " + innerHeight)
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);

        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    useEffect(() => {
        const fetchActiveFloor = async () => {
            const savedFloor = localStorage.getItem('selectedFloor');
            if (!savedFloor) {
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/Floor/active`, {
                        method: 'GET',
                        credentials: 'include',
                    });
                    const data = await response.json();
                    if (data.length > 0) {
                        const defaultFloor = { value: data[0].id, label: data[0].floorName };
                        localStorage.setItem('selectedFloor', JSON.stringify(defaultFloor));
                    }
                    putRef('activeFloors', data).catch(() => {});
                } catch {
                    const cached = await getRef('activeFloors');
                    if (cached && cached.length > 0) {
                        const defaultFloor = { value: cached[0].id, label: cached[0].floorName };
                        localStorage.setItem('selectedFloor', JSON.stringify(defaultFloor));
                    }
                }
            }

            loadSelectedFloorId(setSelectedFloorId, fetchFloor);
        };

        fetchActiveFloor();
    }, []);


    const fetchFloor = async (floorId: string) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Floor/` + floorId, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Error fetching floor');
            const data = await response.json();
            setFloor(data);
            putRef(`floor_${floorId}`, data).catch(() => {});
        } catch (error) {
            console.warn("Fetch floor failed, trying cache: ", error);
            const cached = await getRef(`floor_${floorId}`);
            if (cached) setFloor(cached);
        }
    };

    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'selectedFloor') {
                loadSelectedFloorId(setSelectedFloorId, fetchFloor);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    useEffect(() => {
        loadSelectedFloorId(setSelectedFloorId, fetchFloor);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setShowSpinner(true);
            if (selectedFloorId) {
                await fetchFloor(selectedFloorId);
            }
            setShowSpinner(false);
        };
        fetchData();
    }, [selectedFloorId]);

    // Background polling: refresh table statuses every 25 seconds
    useEffect(() => {
        if (!selectedFloorId) return;

        const interval = setInterval(() => {
            // Skip refresh during merge/transfer/z-report modes
            if (tableToMergeFrom || tableToTransferProductsFrom || isZReportMode) return;
            fetchFloor(selectedFloorId);
        }, 25000);

        return () => clearInterval(interval);
    }, [selectedFloorId, tableToMergeFrom, tableToTransferProductsFrom, isZReportMode]);

    /**
     * Hand the device to the next person.
     *
     * This sits where a refresh button used to, because the floor already reloads
     * itself every 25 seconds and handing over happens dozens of times a service.
     * It ends the staff session only — the venue stays signed in.
     */
    const handleLock = useCallback(async () => {
        await lock();
        navigate('/lock', { replace: true });
    }, [lock, navigate]);

    useEffect(() => {
        if (divRef.current) {
            const { offsetWidth, offsetHeight } = divRef.current;
            console.log("boardsize: " + offsetWidth + "x" + offsetHeight)
            setboardSize({
                width: offsetWidth,
                height: offsetHeight,
            });
        }
    }, []);


    const isTableDisabled = (table: Table) => {
        // In assign mode every active table is a valid target - a poor fit is a
        // hint, not a block, because staff routinely join or over-seat tables.
        if (resv.assigning) return !table.isActive;

        if (tableToMergeFrom != null || tableToTransferProductsFrom) {
            if (tableToTransferProductsFrom && table.id == tableToTransferProductsFrom) {
                return true;
            }
            if (table.hasActiveOrder) return false;
            else return true;
        }
        else if (isZReportMode) {
            return !table.hasActiveOrder;
        }
        else {
            return false;
        }
    };

    const handleReturnToZReport = () => {
        navigate('/close-shift');
    };

    const handleCloseReceipt = () => {
        setIsReceiptOpen(false);
        setActiveOrder(undefined);
        setOrderProducts([]);
    };

    const openCashPayment = () => {
        setIsReceiptOpen(false);
        setIsCashPaymentOpen(true);
    };

    const openCardPayment = () => {
        setIsReceiptOpen(false);
        setIsCardPaymentOpen(true);
    };

    const handleBookOnRoom = () => {
        // Implement if needed
    };

    const [showConfirmTransfer, setsShowConfirmTransfer] = useState(false);
    const [showConfirmMerge, setsShowConfirmMerge] = useState(false);

    const confirmTransfer = () => {
        if (tableToTransferProductsTo) {
            setsShowConfirmTransfer(true);
        }
    }

    const handleTransferConfirmation = async () => {
        if (!can('pos-app.order.transfer')) return;
        try {
            setShowSpinner(true);
            setsShowConfirmTransfer(false);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/transfer-products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tableToTransferFromId: tableToTransferProductsFrom,
                    tableToTransferToId: tableToTransferProductsTo,
                    productsToTransfer: productsToTransfer,
                    combinationsToTransfer: combinationsToTransfer
                }),
                credentials: 'include',
            });
            if (response) {
                localStorage.removeItem('transferFromTable');
                localStorage.removeItem('productsToTransfer');
                localStorage.removeItem('combinationsToTransfer');
                setShowSpinner(false);
                navigate("/tables/" + tableToTransferProductsTo);
            }
        } catch (error) {
            setShowSpinner(false);
            setTitleError("Error!");
            setContentError("There was a problem while transferring products. Please try again.");
            setShowError(true);
            setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
    }

    const confirmMerge = () => {
        setsShowConfirmMerge(true);
    }

    const getNewTransferTable = () => {
        if (floor && floor.tables) {
            const newTable = floor.tables.find(table => table.id == tableToTransferProductsTo);
            if (newTable) {
                return newTable.tableNo;
            }
        }
        return '';

    }

    const getMainTransferTable = () => {
        if (floor && floor.tables) {
            const newTable = floor.tables.find(table => table.id == tableToTransferProductsFrom);
            if (newTable) {
                return newTable.tableNo;
            }
        }

        return '';
    }

    const getMaineMergeTable = () => {
        if (floor && floor.tables) {
            const newTable = floor.tables.find(table => table.id == tableToMergeFrom);
            if (newTable) {
                return newTable.tableNo;
            }
        }
        return '';

    }

    const getNewMergeTables = () => {
        if (floor && floor.tables) {
            const tables = floor.tables.filter(table => selectedTablesToMerge.includes(table.id)).sort((a, b) => a.tableNo.localeCompare(b.tableNo));
            if (tables && tables.length > 0) {
                return tables.map(table => table.tableNo).join(", ");
            }
        }
        return '';

    }

    // ── Floor-view helpers ───────────────────────────────────────────────────
    const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };
    const money = (t?: Table) => {
        if (t?.orderTotal == null) return '';
        const sym = CURRENCY_SYMBOLS[t.currency || ''] || (t.currency ? `${t.currency} ` : '');
        return `${sym}${t.orderTotal.toFixed(0)}`;
    };
    const elapsedMin = (t?: Table): number | null => {
        if (!t?.openedAt) return null;
        return Math.max(0, Math.floor((now - new Date(t.openedAt).getTime()) / 60000));
    };
    const fmtElapsed = (m: number | null) => {
        if (m == null) return '—';
        if (m < 60) return `${m}m`;
        return `${Math.floor(m / 60)}h ${m % 60}m`;
    };
    const isSelected = (t: Table) =>
        selectedTablesToMerge.includes(t.id)
        || (tableToTransferProductsTo != null && tableToTransferProductsTo === t.id)
        || assignPicks.includes(t.id);

    /**
     * How the sitting at this table is running against its turn time. A table
     * seated from a booking is measured against the turn that booking was planned
     * with; a walk-in against the rule for however many covers are on it.
     *
     * Next-booking risk only applies while looking at today — comparing the live
     * clock to a booking the host is browsing for next week would be nonsense.
     */
    const turnFor = (t: Table): TurnStatus | null => {
        if (!t.hasActiveOrder) return null;
        const seated = resv.seatedByTableId[t.id];
        const planned = seated
            ? seated.durationMinutes
            : plannedTurnMinutes(resv.settings, t.covers ?? t.seats);
        const next = resv.byTableId[t.id];
        return turnStatus({
            openedAt: t.openedAt,
            plannedMinutes: planned,
            bufferMinutes: resv.settings?.bufferMinutes ?? 0,
            nextBookingTime: resv.isToday && next ? next.time : null,
            graceMinutes: lateGraceMinutes(resv.settings),
            now,
        });
    };

    /** How the booking a table is being held for is running against the clock. */
    const lateFor = (booking?: Reservation) => (
        booking ? lateStatus(booking, lateGraceMinutes(resv.settings), now) : null
    );

    /**
     * A table sat without a booking behind it — a walk-in. The timeline draws it,
     * because a lane that looks empty has to actually be empty: quoting 20:00 on
     * T5 while somebody is sitting at T5 is the exact mistake a grid exists to
     * prevent. Tables seated *from* a booking are left out — their own block
     * already covers that stretch of the day.
     */
    const occupancyFor = (t: Table): Occupancy | null => {
        if (!t.hasActiveOrder || !t.openedAt || resv.seatedByTableId[t.id]) return null;
        const turn = turnFor(t);
        const opened = new Date(t.openedAt);
        // A table opened before midnight started before the day being drawn.
        const startMin = toDateKey(opened) === resv.dateKey
            ? opened.getHours() * 60 + opened.getMinutes()
            : 0;
        const planned = turn?.planned ?? plannedTurnMinutes(resv.settings, t.covers ?? t.seats);
        return {
            startMin,
            // A sitting already past its turn still holds the table, so the block
            // runs to now rather than stopping where the plan said it would.
            endMin: startMin + Math.max(planned, turn?.elapsed ?? 0),
            covers: t.covers ?? 0,
            over: !!turn && turn.over > 0,
        };
    };

    // Past its planned turn, so the table is worth a second look. Falls back to
    // the old flat 90 minutes only while the reservation settings are still loading.
    const isAttention = (t: Table) => {
        if (!t.hasActiveOrder) return false;
        const turn = turnFor(t);
        return turn ? turn.over > 0 : (elapsedMin(t) ?? 0) >= 90;
    };

    /**
     * The next booking for a table, rendered the same way on state cards and
     * section tiles — both are 230px, so there is no reason for one to show less
     * than the other. The map node has its own 78px-wide version.
     *
     * A `span` rather than a `div` because the section tile is a `<button>`.
     */
    /**
     * A booking's guest, or something sensible when there isn't one. The type says guest is always
     * present; the API disagrees, and a single nameless booking used to take the whole floor plan
     * down with it rather than just showing a blank name.
     */
    const guestLabel = (r: { guest?: { firstName?: string; lastName?: string } | null }) =>
        `${r?.guest?.firstName || 'Guest'} ${r?.guest?.lastName || ''}`.trim();

    const renderBookingStrip = (table: Table, booking: Reservation, turn: TurnStatus | null) => {
        const late = lateFor(booking);
        return (
            <span
                className={`tv-resvstrip${late?.late ? ' is-overdue' : turn && turn.risk > 0 ? ' is-risk' : ''}`}
                title={late?.late
                    // Said in full here because the board has no room for it, and the
                    // cost is the part a host needs: this table is not earning.
                    ? `${guestLabel(booking)} is ${fmtElapsed(late.lateBy)} late — ${table.tableNo} is still being held. Settle it in the reservations panel.`
                    : turn && turn.risk > 0
                        ? turnTitle(table, turn, booking)
                        : `${booking.partySize} guests${booking.guestNotes ? ` · ${booking.guestNotes}` : ''}`}
            >
                <span className='tv-resvstrip__time'>{booking.time}</span>
                <span className='tv-resvstrip__name'>
                    {guestLabel(booking)}
                </span>
                {late?.late
                    ? <span className='tv-resvstrip__late'>{fmtElapsed(late.lateBy)} late</span>
                    : turn && turn.risk > 0
                        ? <span className='tv-resvstrip__late'>~{fmtElapsed(turn.risk)} late</span>
                        : <span className='tv-resvstrip__party'>{booking.partySize}</span>}
            </span>
        );
    };

    /** Spells out the turn maths on hover, since the badges only have room for a number. */
    const turnTitle = (t: Table, turn: TurnStatus | null, next?: Reservation): string | undefined => {
        if (!turn) return undefined;
        const lines: string[] = [];
        if (turn.over > 0) {
            lines.push(`${fmtElapsed(turn.elapsed)} at the table — ${fmtElapsed(turn.over)} past the ${turn.planned} min turn`);
        } else {
            lines.push(`${fmtElapsed(turn.elapsed)} of a ${turn.planned} min turn`);
        }
        if (next && turn.risk > 0) {
            lines.push(`${next.time} ${guestLabel(next)} may be kept waiting ~${fmtElapsed(turn.risk)}`);
        }
        return lines.join('\n');
    };

    const statusClasses = (t: Table): string[] => {
        const out: string[] = [];
        if (isSelected(t)) out.push('is-selected');
        else if (isAttention(t)) out.push('is-attention');
        else if (t.hasActiveOrder) out.push('is-busy');
        if ((isTableDisabled(t) || !t.isActive) && !isSelected(t)) out.push('is-disabled');
        return out;
    };

    // Effective service state: real data (free/occupied + stale) refined by the
    // optional per-device marker staff set via the inline picker.
    const effectiveState = (t: Table): ServiceStateKey => {
        const manual = svcStates[t.id];
        if (t.hasActiveOrder) {
            if (manual && ACTIVE_STATES.includes(manual)) return manual;
            return 'ordered';
        }
        if (manual && CLOSED_STATES.includes(manual)) return manual;
        return 'available';
    };

    const tables = floor?.tables || [];

    /**
     * Every table in the property. The rail lists the whole day while the board shows
     * one floor, so a booking on the Terrace had nothing to resolve its table against
     * and its card read "No table" — inviting a host to assign it a second one.
     */
    const allTables = floors.length ? floors.flatMap((f) => f.tables || []) : tables;

    /**
     * The floor a booking is on, when it is not the floor on screen. Said on the card
     * so "T4 · Terrace" cannot be taken for the T4 in front of you.
     */
    const otherFloorByReservationId: Record<string, string> = {};
    if (floors.length > 1) {
        const floorOfTable: Record<string, Floor> = {};
        floors.forEach((f) => (f.tables || []).forEach((t) => { floorOfTable[t.id] = f; }));
        resv.onDate.forEach((r) => {
            const elsewhere = r.tableIds
                .map((id) => floorOfTable[id])
                .filter((f) => f && f.id !== selectedFloorId)
                .map((f) => f.floorName);
            if (elsewhere.length) {
                otherFloorByReservationId[r.id] = Array.from(new Set(elsewhere)).join(' · ');
            }
        });
    }

    /**
     * Bookings whose table is still occupied and not expected to free up in time,
     * keyed by reservation id with the minutes they are likely to wait. Measured
     * here rather than in the rail because this is where the clock ticks.
     */
    const riskByReservationId: Record<string, number> = {};
    if (resv.isToday) {
        tables.forEach((t) => {
            const booking = resv.byTableId[t.id];
            if (!booking) return;
            const turn = turnFor(t);
            if (turn && turn.risk > 0) {
                // A join can have two late tables; the worst one is what matters.
                riskByReservationId[booking.id] = Math.max(riskByReservationId[booking.id] ?? 0, turn.risk);
            }
        });
    }

    /**
     * What to quote a walk-in of this size: the soonest a table big enough should
     * come free, plus the turnaround buffer. Tables already held for a booking
     * that starts inside this party's turn are not candidates — seating a walk-in
     * there only moves the problem an hour down the service.
     */
    const suggestQuote = (partySize: number): number | undefined => {
        const turnMinutes = plannedTurnMinutes(resv.settings, partySize);
        const buffer = resv.settings?.bufferMinutes ?? 0;
        const clock = new Date(now);
        const nowMinutes = clock.getHours() * 60 + clock.getMinutes();
        const waits: number[] = [];
        tables.forEach((t) => {
            if (!t.isActive || t.seats < partySize) return;
            const turn = turnFor(t);
            const freeIn = t.hasActiveOrder && turn ? Math.max(0, turn.planned - turn.elapsed) + buffer : 0;
            const held = resv.isToday ? resv.byTableId[t.id] : undefined;
            // A booking nobody has seated and nobody has vouched for stops counting
            // against the quote: it was making the room sound busier than it is, and
            // a quote is an estimate the host is free to overrule. The table itself
            // stays theirs — only `No show` releases it, because a table must never
            // be given away by a clock running out.
            const stillTheirs = held && !lateFor(held)?.late;
            if (held && stillTheirs && minutesOfTime(held.time) < nowMinutes + freeIn + turnMinutes) return;
            waits.push(freeIn);
        });
        return waits.length > 0 ? Math.min(...waits) : undefined;
    };

    const serviceCounts: Record<ServiceStateKey, number> = (() => {
        const c = Object.fromEntries(SERVICE_STATE_ORDER.map((k) => [k, 0])) as Record<ServiceStateKey, number>;
        tables.forEach((t) => { c[effectiveState(t)] += 1; });
        return c;
    })();

    const stateKpis = (() => {
        const occupied = tables.filter((t) => !!t.hasActiveOrder);
        const covers = occupied.reduce((s, t) => s + (t.covers || 0), 0);
        const openTotal = occupied.reduce((s, t) => s + (t.orderTotal || 0), 0);
        const needsAction = tables.filter((t) => {
            const st = effectiveState(t);
            return st === 'check' || st === 'dirty' || isAttention(t);
        }).length;
        const occPct = tables.length ? Math.round((occupied.length / tables.length) * 100) : 0;
        return { covers, openChecks: occupied.length, openTotal, needsAction, occPct };
    })();

    const totalsMoney = (n: number) => {
        const cur = tables.find((t) => t.currency)?.currency || '';
        const sym = CURRENCY_SYMBOLS[cur] || (cur ? `${cur} ` : '€');
        return `${sym}${n.toFixed(0)}`;
    };

    // ── Sections (group occupied tables by their server / waiter) ────────────
    const sectionLoad = (() => {
        const map = new Map<string, { name: string; color: string; tables: number; covers: number; sales: number }>();
        tables.forEach((t) => {
            if (!t.hasActiveOrder || !t.waiterName) return;
            const key = t.waiterName;
            const entry = map.get(key) || { name: key, color: serverColor(key), tables: 0, covers: 0, sales: 0 };
            entry.tables += 1;
            entry.covers += t.covers || 0;
            entry.sales += t.orderTotal || 0;
            map.set(key, entry);
        });
        return Array.from(map.values()).sort((a, b) => b.sales - a.sales);
    })();

    const myServerName = (() => {
        if (!currentUserName) return '';
        const match = sectionLoad.find((s) => s.name.toLowerCase() === currentUserName.toLowerCase());
        return match ? match.name : currentUserName;
    })();

    const isSectionDimmed = (t: Table): boolean => {
        if (sectionScope === 'all') return false;
        if (sectionScope === 'mine') return t.waiterName !== myServerName;
        return t.waiterName !== sectionScope;
    };

    const dockVisible = !isZReportMode && !tableToMergeFrom && !tableToTransferProductsFrom && !resv.assigning;
    const inSpecialMode = isZReportMode || !!tableToMergeFrom || !!tableToTransferProductsFrom || !!resv.assigning;

    // Merging, transferring and Z-reports are about orders rather than bookings, and
    // they work by tapping tables where the room says they are. Both the rail and
    // the timeline step aside for those — but not for assigning, which is a
    // reservation flow and is the one thing the timeline is best at picking for.
    const orderCentricMode = isZReportMode || !!tableToMergeFrom || !!tableToTransferProductsFrom;
    const railShown = canSeeReservations && railOpen && !orderCentricMode;
    const timelineShown = canSeeReservations && viewMode === 'timeline' && !orderCentricMode;
    const boardView: 'map' | 'states' | 'sections' = viewMode === 'timeline' ? 'map' : viewMode;

    // ── Assign mode: capacity + conflict checks against the picked booking ────
    const assignCovers = resv.assigning?.partySize ?? 0;
    /** Seats across the tables already staged, so joins can cover a big party. */
    const pickedSeats = assignPicks.reduce(
        (sum, id) => sum + (tables.find((t) => t.id === id)?.seats || 0), 0,
    );
    const assignSeatsShort = Math.max(0, assignCovers - pickedSeats);

    /** Bookings that would clash with this table at the same time. */
    const conflictFor = (table: Table): Reservation | null => {
        const booking = resv.assigning;
        if (!booking) return null;
        const start = minutesOfTime(booking.time);
        const buffer = resv.settings?.bufferMinutes ?? 0;
        return resv.onDate.find((other) => {
            if (other.id === booking.id) return false;
            if (!other.tableIds.includes(table.id)) return false;
            if (['cancelled', 'noShow', 'completed'].includes(other.status)) return false;
            const oStart = minutesOfTime(other.time);
            return oStart < start + booking.durationMinutes + buffer
                && start < oStart + other.durationMinutes + buffer;
        }) || null;
    };

    /**
     * Clashes for the tables staged so far. Computed from what the board already
     * knows, so the warning is up before the button is pressed rather than after a
     * refused round trip; `refusedClashes` covers what only the server can see —
     * a booking on another floor, or one another device took a moment ago.
     */
    const stagedClashes: ReservationClash[] = assignPicks.flatMap((id) => {
        const table = tables.find((t) => t.id === id);
        const other = table ? conflictFor(table) : null;
        if (!table || !other) return [];
        return [{
            tableId: id,
            tableNo: table.tableNo,
            reservationId: other.id,
            code: other.code,
            time: other.time,
            partySize: other.partySize,
            guestName: guestLabel(other),
        }];
    });

    const assignClashes = refusedClashes.length > 0 ? refusedClashes : stagedClashes;

    /**
     * Joins worth offering when no single table can take the party. A host seating ten
     * otherwise has to work out which tables are free, which are big enough together
     * and which are close enough to actually push together — while the party stands at
     * the door. Ranked by fewest tables, then fewest empty seats, then closest
     * together, and staged rather than saved so the board shows the answer first.
     */
    const assignSuggestions: { tables: Table[]; seats: number; spread: number }[] = (() => {
        const booking = resv.assigning;
        if (!booking) return [];
        const covers = booking.partySize;
        // A party at the door needs a table that is empty now; a booking later tonight
        // can be promised one that somebody is still sitting at.
        const wantsATableNow = !!booking.waitlist || booking.status === 'arrived';
        const candidates = tables.filter((t) => (
            t.isActive
            && !conflictFor(t)
            && (!wantsATableNow || !t.hasActiveOrder)
        ));
        // One table can take them: the host can see that without being told.
        if (candidates.some((t) => t.seats >= covers)) return [];

        const centre = (t: Table) => ({
            x: t.distanceLeft + (t.baseSize * (t.width || 1)) / 2,
            y: t.distanceAbove + (t.baseSize * (t.height || 1)) / 2,
        });
        const apart = (a: Table, b: Table) => {
            const [p, q] = [centre(a), centre(b)];
            return Math.hypot(p.x - q.x, p.y - q.y);
        };
        // Tables across the room cannot be pushed together, so distance is a hard
        // limit rather than a preference. Measured against the tables' own size so it
        // holds whatever scale the floor was drawn at.
        const joinable = (a: Table, b: Table) => apart(a, b) <= 4.5 * ((a.baseSize + b.baseSize) / 2);

        const found: { tables: Table[]; seats: number; spread: number }[] = [];
        for (let i = 0; i < candidates.length; i++) {
            for (let j = i + 1; j < candidates.length; j++) {
                const [a, b] = [candidates[i], candidates[j]];
                if (!joinable(a, b)) continue;
                if (a.seats + b.seats >= covers) {
                    found.push({ tables: [a, b], seats: a.seats + b.seats, spread: apart(a, b) });
                    continue; // A third table on top of a pair that already fits is waste.
                }
                for (let k = j + 1; k < candidates.length; k++) {
                    const c = candidates[k];
                    // Chained to one of the two, so the three form a run rather than
                    // a pair plus something on the far side of it.
                    if (!joinable(b, c) && !joinable(a, c)) continue;
                    if (a.seats + b.seats + c.seats < covers) continue;
                    found.push({
                        tables: [a, b, c],
                        seats: a.seats + b.seats + c.seats,
                        spread: Math.max(apart(a, b), apart(b, c), apart(a, c)),
                    });
                }
            }
        }
        found.sort((p, q) => (
            p.tables.length - q.tables.length
            || (p.seats - covers) - (q.seats - covers)
            || p.spread - q.spread
        ));
        return found.slice(0, 2);
    })();

    /** Too small on its own and nothing staged yet — worth dimming as a hint. */
    const isAssignPoorFit = (table: Table): boolean => {
        if (!resv.assigning) return false;
        if (assignPicks.includes(table.id)) return false;
        return table.seats < assignCovers && assignPicks.length === 0;
    };

    const reservationFor = (table: Table): Reservation | undefined => resv.byTableId[table.id];

    const ic = {
        people: <svg viewBox="0 0 24 24" width="11" height="11" fill="none"><path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM22 19v-1a4 4 0 0 0-3-3.87M16 5.13A4 4 0 0 1 16 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        clock: <svg viewBox="0 0 24 24" width="11" height="11" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        map: <svg viewBox="0 0 24 24" fill="none"><path d="M9 3 3 5.5v15L9 18m0-15 6 3m-6-3v15m6-12 6-2.5v15L15 21m0-15v15m0 0-6-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        grid: <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" /><rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" /><rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" /><rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" /></svg>,
        states: <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" /><rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" fill="currentColor" fillOpacity="0.18" /><rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" fill="currentColor" fillOpacity="0.18" /><rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" /></svg>,
        sections: <svg viewBox="0 0 24 24" fill="none"><circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" /><circle cx="16.5" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" /><path d="M3 19a5 5 0 0 1 10 0M11.5 19a5 5 0 0 1 9.5-2.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
        sun: <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" /><path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
        moon: <svg viewBox="0 0 24 24" fill="none"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>,
        lock: <svg viewBox="0 0 24 24" fill="none"><rect x="4" y="10.5" width="16" height="10.5" rx="2.5" stroke="currentColor" strokeWidth="1.8" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
        calendar: <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" /><path d="M3 10h18M8 3v4m8-4v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
        timeline: <svg viewBox="0 0 24 24" fill="none"><path d="M3 5h18M3 12h18M3 19h18" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeLinecap="round" /><rect x="4" y="3" width="9" height="4" rx="1.6" stroke="currentColor" strokeWidth="1.8" /><rect x="10" y="10" width="10" height="4" rx="1.6" stroke="currentColor" strokeWidth="1.8" /><rect x="6" y="17" width="7" height="4" rx="1.6" stroke="currentColor" strokeWidth="1.8" /></svg>,
        check: <svg viewBox="0 0 24 24" width="22" height="22" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
        star: <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M12 2.5l2.6 5.7 6.2.6-4.7 4.1 1.4 6.1L12 16l-5.5 3.1 1.4-6.1-4.7-4.1 6.2-.6L12 2.5Z" /></svg>,
    };

    /**
     * The table-status menu, rendered once into <body> rather than inside the table's card.
     *
     * As a descendant of the card it was `position: absolute; top: 42px`, so it always dropped
     * downward and was clipped twice over — by .tv-board-wrap's `overflow: auto` and by the
     * bottom of the screen — leaving the status buttons unreachable for tables low on the map.
     * Portalled and fixed-positioned, it is measured against the viewport instead: it opens
     * below the badge when there is room, flips above it when there is not, clamps to whichever
     * side has more space, and scrolls internally if neither side can show it whole.
     */
    const renderStateMenu = () => {
        if (!stateMenuFor || !stateMenuAnchor) return null;
        const table = tables.find((t) => t.id === stateMenuFor);
        if (!table) return null;

        const busy = !!table.hasActiveOrder;
        const st = effectiveState(table);
        const options = SERVICE_STATE_ORDER.filter((k) => (busy ? k !== 'available' : true));

        const MENU_WIDTH = 168;   // keep in step with .tv-stmenu min-width
        const GAP = 6;            // breathing room between badge and menu
        const EDGE = 8;           // never sit flush against the screen edge
        const ITEM_H = 33;        // item height incl. gap, for the fits-below estimate
        const estimated = options.length * ITEM_H + 16;

        const anchor = stateMenuAnchor;
        const spaceBelow = window.innerHeight - anchor.bottom - GAP - EDGE;
        const spaceAbove = anchor.top - GAP - EDGE;
        const openUp = spaceBelow < estimated && spaceAbove > spaceBelow;

        // Right edges aligned with the badge, as before, then pulled back inside the viewport.
        const left = Math.min(
            Math.max(EDGE, anchor.right - MENU_WIDTH),
            Math.max(EDGE, window.innerWidth - MENU_WIDTH - EDGE),
        );

        const style: React.CSSProperties = {
            left,
            width: MENU_WIDTH,
            maxHeight: Math.max(120, openUp ? spaceAbove : spaceBelow),
            ...(openUp
                ? { bottom: window.innerHeight - anchor.top + GAP }
                : { top: anchor.bottom + GAP }),
        };

        return createPortal(
            <>
                <div className='tv-stmenu-scrim' onClick={closeStateMenu} />
                <div className='tv-stmenu' style={style} onClick={(e) => e.stopPropagation()}>
                    {options.map((k) => (
                        <button
                            key={k}
                            type='button'
                            className={`tv-stmenu__item${k === st ? ' is-current' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setTableServiceState(table.id, k); }}
                        >
                            <span className='tv-stmenu__dot' style={{ background: SERVICE_STATES[k].accent }} />
                            {SERVICE_STATES[k].label}
                        </button>
                    ))}
                </div>
            </>,
            document.body,
        );
    };

    /**
     * The smallest the map is allowed to be drawn, from the screen the floor was
     * laid out on. When it is bigger than the space available the board overflows
     * and the container scrolls, rather than the room folding in on itself.
     */
    const floorExtent = useMemo(() => {
        const items = [...(floor?.tables || []), ...(floor?.walls || []), ...(floor?.decorations || [])];
        const designW = Math.max(0, ...items.map(i => i.screenWidthOnUpdate || 0));
        const designH = Math.max(0, ...items.map(i => i.screenHeightOnUpdate || 0));
        return {
            width: Math.round(designW * MIN_FLOOR_SCALE),
            height: Math.max(MIN_BOARD_HEIGHT, Math.round(designH * MIN_FLOOR_SCALE)),
        };
    }, [floor]);

    /**
     * How much to shrink a layout saved at `designW × designH` to fit the space
     * the board has — but never past MIN_FLOOR_SCALE, below which tables start
     * landing on each other. Under that the map keeps its size and overflows.
     */
    const layoutScale = (designW: number, designH: number) => ({
        x: Math.max(MIN_FLOOR_SCALE, designW > 0 ? boardSize.width / designW : 0),
        y: Math.max(MIN_FLOOR_SCALE, designH > 0 ? boardSize.height / designH : 0),
    });

    const renderNode = (table: Table) => {
        const widthScale = boardSize.width / table.screenWidthOnUpdate;
        const heightScale = boardSize.height / table.screenHeightOnUpdate;
        const rawScale = Math.min(heightScale, widthScale);
        const scalingFactor = Math.max(0.55, Math.min(rawScale || 0.55, 1.1));
        const scaledX = table.distanceLeft * widthScale;
        const scaledY = table.distanceAbove * heightScale;
        // Uniform node: every table is the same size + shape regardless of type/seats.
        const baseH = Math.max(66, table.baseSize * scalingFactor);
        const nodeW = baseH * 1.18;
        const numSize = Math.max(15, Math.min(19, baseH * 0.25));
        const disabled = (isTableDisabled(table) || !table.isActive) && !isSelected(table);
        const booking = reservationFor(table);
        const clash = conflictFor(table);
        const showResv = !!booking && !resv.assigning;
        const turn = turnFor(table);
        const classes = [
            'tv-node',
            ...statusClasses(table),
            showResv ? 'has-resv' : '',
            isAssignPoorFit(table) ? 'is-poorfit' : '',
            clash ? 'is-clash' : '',
        ].filter(Boolean).join(' ');
        return (
            <button
                key={table.id}
                className={classes}
                onClick={(e) => handleTableClick(e, table)}
                style={{ position: 'absolute', left: scaledX, top: scaledY, width: nodeW, height: baseH, cursor: disabled ? 'default' : 'pointer' }}
                title={clash
                    ? `Clashes with ${clash.time} ${guestLabel(clash)} (${clash.partySize})`
                    : turnTitle(table, turn, booking)}
            >
                <span className='tv-node__dot' />
                {table.vipTable && <span className='tv-node__vip'>{ic.star}</span>}
                <span className='tv-node__no' style={{ fontSize: numSize }}>{table.tableNo}</span>
                {table.hasActiveOrder ? (
                    <span className='tv-node__amount'>
                        {money(table)}
                        {turn && turn.over > 0 && <i className='tv-overpill'>+{fmtElapsed(turn.over)}</i>}
                    </span>
                ) : (
                    <span className='tv-node__meta'>{ic.people}{table.seats}</span>
                )}
                {showResv && (
                    <span
                        className={`tv-node__resv${lateFor(booking)?.late
                            ? ' is-overdue'
                            : turn && turn.risk > 0 ? ' is-risk' : ''}`}
                    >
                        {booking!.time} · {booking!.partySize}
                    </span>
                )}
                {isSelected(table) && <span className='tv-node__check'>{ic.check}</span>}
            </button>
        );
    };

    const renderStateCard = (table: Table) => {
        const st = effectiveState(table);
        const def = SERVICE_STATES[st];
        const busy = !!table.hasActiveOrder;
        const disabled = (isTableDisabled(table) || !table.isActive) && !isSelected(table);
        const stale = isAttention(table) || st === 'check';
        const selectable = !inSpecialMode; // outside merge/transfer/zreport, allow state edits
        const booking = reservationFor(table);
        const clash = conflictFor(table);
        const turn = turnFor(table);
        const classes = [
            'tv-stcard',
            ...statusClasses(table),
            stale && (st === 'check' || isAttention(table)) ? 'is-stale' : '',
            isAssignPoorFit(table) ? 'is-poorfit' : '',
            clash ? 'is-clash' : '',
        ].filter(Boolean).join(' ');
        return (
            <div
                key={table.id}
                className={classes}
                style={{ ['--accent' as any]: def.accent, ['--tint' as any]: def.tint, cursor: disabled ? 'default' : 'pointer' }}
                onClick={(e) => handleTableClick(e, table)}
                role='button'
                tabIndex={disabled ? -1 : 0}
            >
                <span className='tv-stcard__bar' />
                <div className='tv-stcard__head'>
                    <span className='tv-stcard__no'>{table.tableNo}{table.vipTable && <i className='tv-stcard__vip'>VIP</i>}</span>
                    {selectable && !disabled ? (
                        <button
                            type='button'
                            className='tv-stcard__badge'
                            onClick={(e) => { e.stopPropagation(); toggleStateMenu(table.id, e.currentTarget); }}
                        >
                            {def.label}
                            <svg viewBox='0 0 24 24' width='11' height='11' fill='none'><path d='m6 9 6 6 6-6' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' /></svg>
                        </button>
                    ) : (
                        <span className='tv-stcard__badge tv-stcard__badge--static'>{def.label}</span>
                    )}
                </div>

                {busy ? (
                    <div className='tv-stcard__body'>
                        <div className='tv-stcard__rows'>
                            <span className='tv-stcard__meta'>{ic.people}{table.covers ?? '—'}/{table.seats}</span>
                            {/* Elapsed against the planned turn, with the same overrun pill
                              * the other two views use — kept in one group so it can't wrap
                              * away from the clock it belongs to. */}
                            <span className='tv-stcard__meta' title={turnTitle(table, turn, booking)}>
                                {ic.clock}
                                <span>
                                    {fmtElapsed(elapsedMin(table))}
                                    {/* The planned turn is only worth showing while the table is
                                      * still inside it — once it is over, the pill says the same
                                      * thing and the row has no room for both. */}
                                    {turn && turn.over === 0 && <i className='tv-stcard__turn'> / {turn.planned}m</i>}
                                </span>
                                {turn && turn.over > 0 && (
                                    <span className='tv-overpill'>+{fmtElapsed(turn.over)}</span>
                                )}
                            </span>
                            {table.orderTotal != null && <span className='tv-stcard__meta tv-stcard__meta--amt'>{money(table)}</span>}
                        </div>
                        <div className='tv-stcard__foot'>
                            {table.waiterName && <span className='tv-stcard__server'>{table.waiterName}</span>}
                            <span className='tv-stcard__hint'>{isAttention(table) ? 'Check back now' : def.hint}</span>
                        </div>
                    </div>
                ) : (
                    <div className='tv-stcard__body tv-stcard__body--empty'>
                        <span className='tv-stcard__seats'>{table.seats} seats</span>
                        <span className='tv-stcard__cta'>{st === 'dirty' ? 'Bus & reset' : 'Tap to seat'}</span>
                    </div>
                )}

                {booking && !resv.assigning && renderBookingStrip(table, booking, turn)}

                {/* The state menu itself is rendered once, portalled to <body> — see
                    renderStateMenu above. Kept inside this card it could not escape
                    .tv-board-wrap's overflow and was clipped by the bottom of the screen. */}
                {isSelected(table) && <span className='tv-node__check'>{ic.check}</span>}
            </div>
        );
    };

    const renderSectionTile = (table: Table) => {
        const busy = !!table.hasActiveOrder;
        const sv = busy && table.waiterName ? table.waiterName : '';
        const color = sv ? serverColor(sv) : 'var(--color-border-strong)';
        const dim = isSectionDimmed(table);
        const mine = !!sv && sv === myServerName;
        const disabled = (isTableDisabled(table) || !table.isActive) && !isSelected(table);
        const booking = reservationFor(table);
        const turn = turnFor(table);
        const classes = [
            'tv-sectile',
            ...statusClasses(table),
            dim ? 'is-dim' : '',
            mine ? 'is-mine' : '',
            !sv ? 'is-unassigned' : '',
            isAssignPoorFit(table) ? 'is-poorfit' : '',
            conflictFor(table) ? 'is-clash' : '',
        ].filter(Boolean).join(' ');
        return (
            <button
                key={table.id}
                className={classes}
                style={{ ['--c' as any]: color, cursor: disabled ? 'default' : 'pointer' }}
                onClick={(e) => handleTableClick(e, table)}
                title={turnTitle(table, turn, booking)}
            >
                <span className='tv-sectile__bar' />
                <div className='tv-sectile__head'>
                    <span className='tv-sectile__no'>{table.tableNo}</span>
                    {sv ? (
                        <span className='tv-sectile__chip' title={sv}>{initialsOf(sv)}</span>
                    ) : (
                        <span className='tv-sectile__chip tv-sectile__chip--none'>—</span>
                    )}
                </div>
                {busy ? (
                    <div className='tv-sectile__body'>
                        <span className='tv-sectile__meta'>{table.covers ?? '—'}/{table.seats} cov</span>
                        {turn && turn.over > 0 && (
                            <span className='tv-overpill'>+{fmtElapsed(turn.over)}</span>
                        )}
                        <span className='tv-sectile__amt'>{money(table)}</span>
                    </div>
                ) : (
                    <div className='tv-sectile__body tv-sectile__body--free'>
                        <span className='tv-sectile__free'>Free · {table.seats}</span>
                    </div>
                )}
                {booking && !resv.assigning && renderBookingStrip(table, booking, turn)}
                {isSelected(table) && <span className='tv-node__check'>{ic.check}</span>}
            </button>
        );
    };

    const renderWall = (wall: any) => {
        const { x: widthScale, y: heightScale } = layoutScale(wall.screenWidthOnUpdate, wall.screenHeightOnUpdate);
        return (
            <div
                key={wall.id}
                className='tv-wall'
                style={{
                    left: wall.distanceLeft * widthScale,
                    top: wall.distanceAbove * heightScale,
                    width: Math.max(6, wall.baseSize * wall.width * widthScale),
                    height: Math.max(6, wall.baseSize * wall.height * heightScale * 0.18),
                    transform: `rotate(${wall.rotation}deg)`,
                }}
            />
        );
    };

    const renderZone = (deco: any) => {
        const { x: widthScale, y: heightScale } = layoutScale(deco.screenWidthOnUpdate, deco.screenHeightOnUpdate);
        const sf = Math.min(widthScale, heightScale);
        return (
            <div
                key={deco.id}
                className='tv-zone'
                style={{
                    left: deco.distanceLeft * widthScale,
                    top: deco.distanceAbove * heightScale,
                    width: deco.baseSize * deco.width * sf,
                    height: deco.baseSize * deco.height * sf,
                }}
            >
                {deco.name || ''}
            </div>
        );
    };

    return (
        <div className='tables-view tv-floorplan ps-scope'>
            <header className='tv-header'>
                <div className='tv-header__lead'>
                    <div className='tv-header__title'>Floor</div>
                    {dockVisible && floors.length > 0 && (
                        <div className='tv-floors'>
                            {floors.map((f) => {
                                const count = (f.tables || []).filter((t) => t.hasActiveOrder).length;
                                return (
                                    <button
                                        key={f.id}
                                        className={`tv-pill ${f.id === selectedFloorId ? 'is-active' : ''}`}
                                        onClick={() => switchFloor(f)}
                                    >
                                        {f.floorName}
                                        {count > 0 && <span className='tv-pill__count'>{count}</span>}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className='tv-header__tools'>
                    <div className='tv-seg'>
                        <button className={`tv-iconbtn ${viewMode === 'map' ? 'is-active' : ''}`} onClick={() => setView('map')} aria-label='Map view' title='Map'>{ic.map}</button>
                        <button className={`tv-iconbtn ${viewMode === 'states' ? 'is-active' : ''}`} onClick={() => setView('states')} aria-label='Service states view' title='Service states'>{ic.states}</button>
                        <button className={`tv-iconbtn ${viewMode === 'sections' ? 'is-active' : ''}`} onClick={() => setView('sections')} aria-label='Server sections view' title='Server sections'>{ic.sections}</button>
                        {canSeeReservations && (
                            <button
                                className={`tv-iconbtn ${viewMode === 'timeline' ? 'is-active' : ''}`}
                                onClick={() => setView('timeline')}
                                disabled={orderCentricMode}
                                aria-label='Timeline view'
                                title={orderCentricMode ? 'Not available while a table is being moved' : 'Timeline - the day by table and time'}
                            >{ic.timeline}</button>
                        )}
                    </div>
                    {canSeeReservations && (
                        <button
                            className={`tv-iconbtn tv-iconbtn--solo ${railOpen ? 'is-active' : ''}`}
                            onClick={toggleRail}
                            aria-label={railOpen ? 'Hide reservations' : 'Show reservations'}
                            aria-pressed={railOpen}
                            title={railOpen ? 'Hide reservations' : 'Show reservations'}
                        >
                            {ic.calendar}
                            {!railOpen && resv.kpis.bookings > 0 && (
                                <span className='tv-iconbtn__badge'>{resv.kpis.bookings}</span>
                            )}
                        </button>
                    )}
                    <button
                        className='tv-iconbtn tv-iconbtn--solo'
                        onClick={handleLock}
                        aria-label='Lock — next person'
                        title='Lock — next person'
                    >
                        {ic.lock}
                    </button>
                </div>
            </header>

            {tableToMergeFrom != null && !isZReportMode && (
                <div className='tv-actionbar'>
                    <span className='tv-actionbar__label'>Select tables to merge into <strong>{getMaineMergeTable()}</strong></span>
                    <div className='tv-actionbar__btns'>
                        <button className='blue-outline-btn' onClick={cancelMerge}>Cancel</button>
                        <button className='blue-filled-btn' onClick={confirmMerge}>Confirm Merge</button>
                    </div>
                </div>
            )}

            {tableToTransferProductsFrom != null && !isZReportMode && (
                <div className='tv-actionbar'>
                    <span className='tv-actionbar__label'>Pick a destination table to transfer to</span>
                    <div className='tv-actionbar__btns'>
                        <button className='blue-outline-btn' onClick={cancelTransfer}>Cancel</button>
                        <button className={tableToTransferProductsTo ? 'blue-filled-btn' : 'gray-filled-btn'} onClick={confirmTransfer} style={{ cursor: !tableToTransferProductsTo ? 'default' : 'pointer' }}>Confirm Transfer</button>
                    </div>
                </div>
            )}

            {/* Service-states sub-bar: KPI strip + legend filters */}
            {viewMode === 'states' && dockVisible && tables.length > 0 && (
                <div className='tv-subbar'>
                    <div className='tv-kpis'>
                        <div className='tv-kpi'><span className='tv-kpi__k'>Covers seated</span><span className='tv-kpi__v'>{stateKpis.covers}</span></div>
                        <div className='tv-kpi'><span className='tv-kpi__k'>Open checks</span><span className='tv-kpi__v'>{stateKpis.openChecks} · {totalsMoney(stateKpis.openTotal)}</span></div>
                        <div className='tv-kpi'><span className='tv-kpi__k'>Occupancy</span><span className='tv-kpi__v'>{stateKpis.occPct}%</span></div>
                        <div className={`tv-kpi${stateKpis.needsAction > 0 ? ' tv-kpi--alert' : ''}`}><span className='tv-kpi__k'>Needs action</span><span className='tv-kpi__v'>{stateKpis.needsAction}</span></div>
                    </div>
                    <div className='tv-legend2'>
                        <button className={`tv-leg${stateFilter === 'all' ? ' is-active' : ''}`} onClick={() => setStateFilter('all')}>
                            <span className='tv-leg__dot' style={{ background: 'var(--color-foreground)' }} />All<span className='tv-leg__n'>{tables.length}</span>
                        </button>
                        {SERVICE_STATE_ORDER.map((k) => (
                            <button key={k} className={`tv-leg${stateFilter === k ? ' is-active' : ''}`} onClick={() => setStateFilter(stateFilter === k ? 'all' : k)}>
                                <span className='tv-leg__dot' style={{ background: SERVICE_STATES[k].accent }} />{SERVICE_STATES[k].label}<span className='tv-leg__n'>{serviceCounts[k]}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Server-sections sub-bar: scope toggles + per-server load cards */}
            {viewMode === 'sections' && dockVisible && tables.length > 0 && (
                <div className='tv-subbar'>
                    <div className='tv-secctrls'>
                        <button className={`tv-scopebtn${sectionScope === 'all' ? ' is-active' : ''}`} onClick={() => setSectionScope('all')}>All sections</button>
                        <button className={`tv-scopebtn${sectionScope === 'mine' ? ' is-active' : ''}`} onClick={() => setSectionScope('mine')}>
                            {myServerName && <span className='tv-secdot' style={{ background: serverColor(myServerName) }} />} My tables
                        </button>
                        {sectionLoad.length > 0 && <span className='tv-secctrls__sep' />}
                        {sectionLoad.map((l) => (
                            <button
                                key={l.name}
                                className={`tv-seccard${sectionScope === l.name ? ' is-active' : ''}`}
                                onClick={() => setSectionScope(sectionScope === l.name ? 'all' : l.name)}
                                style={{ ['--c' as any]: l.color }}
                            >
                                <span className='tv-seccard__avatar'>{initialsOf(l.name)}</span>
                                <span className='tv-seccard__info'>
                                    <span className='tv-seccard__name'>{l.name}{l.name === myServerName && <i className='tv-seccard__you'>you</i>}</span>
                                    <span className='tv-seccard__stats'>{l.tables} tbl · {l.covers} cov · {totalsMoney(l.sales)}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {resv.assigning && (
                <div className='tv-actionbar'>
                    <span className='tv-actionbar__label'>
                        Pick tables for <strong>
                            {/* A queue entry's time is when they walked in, which would
                              * read like a booking time here. Say the wait instead. */}
                            {resv.assigning.waitlist
                                ? ''
                                : `${resv.assigning.time} `}
                            {guestLabel(resv.assigning)}
                        </strong>
                        {resv.assigning.waitlist && (
                            <>{' · '}waiting {fmtElapsed(waitStatus(resv.assigning, now).waited)}</>
                        )}
                        {' · '}{resv.assigning.partySize} {resv.assigning.partySize === 1 ? 'guest' : 'guests'}
                        {assignPicks.length > 0 && (
                            <span className='tv-actionbar__sub'>
                                {' '}— {pickedSeats} seats staged
                                {assignSeatsShort > 0 ? ` · ${assignSeatsShort} short` : ' · fits'}
                            </span>
                        )}
                        {/* Named before the button is pressed: the tables are highlighted
                          * on the board, but the clash is the reason not to press it. */}
                        {assignClashes.length > 0 && (
                            <span className='tv-actionbar__warn'>
                                {assignClashes
                                    .map(c => `${c.tableNo || c.tableId} is taken at ${c.time} by ${c.guestName || 'another booking'}`)
                                    .join(' · ')}
                            </span>
                        )}
                        {/* Only until the staged tables cover the party: after that the
                          * host has an answer and the suggestion is just noise. */}
                        {assignSuggestions.length > 0 && assignSeatsShort > 0 && (
                            <span className='tv-actionbar__joins'>
                                <span>No single table fits {assignCovers} — try</span>
                                {assignSuggestions.map((s) => (
                                    <button
                                        key={s.tables.map(t => t.id).join('+')}
                                        type='button'
                                        className='tv-join'
                                        onClick={() => setAssignPicks(s.tables.map(t => t.id))}
                                    >
                                        {s.tables.map(t => t.tableNo).join(' + ')}
                                        <span className='tv-join__seats'>{s.seats} seats</span>
                                    </button>
                                ))}
                            </span>
                        )}
                    </span>
                    <div className='tv-actionbar__btns'>
                        <button className='blue-outline-btn' onClick={abortAssign}>Cancel</button>
                        <button
                            className={assignPicks.length > 0 ? 'blue-filled-btn' : 'gray-filled-btn'}
                            onClick={confirmAssign}
                        >
                            {assignPicks.length === 0
                                ? 'Clear table'
                                : assignClashes.length > 0
                                    ? 'Assign anyway'
                                    : `Assign ${assignPicks.length} table${assignPicks.length === 1 ? '' : 's'}`}
                        </button>
                    </div>
                </div>
            )}

            {renderStateMenu()}

            <div className={`tv-stage${railShown ? ` has-rail is-${railSide}` : ''}`}>
                {railShown && (
                    <ReservationsRail
                        resv={resv}
                        tables={allTables}
                        now={now}
                        riskByReservationId={riskByReservationId}
                        otherFloorByReservationId={otherFloorByReservationId}
                        side={railSide}
                        onSideChange={changeRailSide}
                        onAssign={beginAssign}
                        onSeat={handleSeat}
                        onEdit={openSheet}
                        onCreate={() => openSheet(null)}
                        onAddWaiting={() => { setWaitSheetFor(null); setWaitSheetOpen(true); }}
                        onEditWaiting={(r) => { setWaitSheetFor(r); setWaitSheetOpen(true); }}
                        canCreate={can('pos-app.reservation.create')}
                    />
                )}

                    {timelineShown && (
                        <ServiceTimeline
                            resv={resv}
                            tables={tables}
                            now={now}
                            occupancyFor={occupancyFor}
                            lateFor={lateFor}
                            otherFloorByReservationId={otherFloorByReservationId}
                            onOpenBooking={openSheet}
                            onAssign={beginAssign}
                            onCreateAt={(time, tableId) => openSheet(null, {
                                time,
                                tableIds: tableId ? [tableId] : [],
                            })}
                            onMove={resv.move}
                            assigning={resv.assigning}
                            assignPicks={assignPicks}
                            onPickTable={toggleTableForAssign}
                            canCreate={can('pos-app.reservation.create')}
                            canEdit={can('pos-app.reservation.edit')}
                        />
                    )}

                {!timelineShown && (
                <div className='tv-board-wrap' onClick={() => stateMenuFor && closeStateMenu()}>
                {boardView === 'map' && (
                    <div
                        className='tv-board'
                        ref={divRef}
                        style={{ minWidth: floorExtent.width, minHeight: floorExtent.height }}
                    >
                        {floor?.walls?.map(renderWall)}
                        {floor?.decorations?.map(renderZone)}
                        {floor?.tables?.map(renderNode)}
                        {(!floor?.tables || floor.tables.length === 0) && (
                            <div className='tv-empty'>
                                <span className='tv-empty__title'>No tables on this floor</span>
                                <span>Pick another floor above.</span>
                            </div>
                        )}
                    </div>
                )}
                {boardView === 'states' && (
                    <div className='tv-grid'>
                        {tables.filter((t) => stateFilter === 'all' || effectiveState(t) === stateFilter).map(renderStateCard)}
                        {tables.length === 0 && (
                            <div className='tv-empty'>
                                <span className='tv-empty__title'>No tables on this floor</span>
                                <span>Pick another floor above.</span>
                            </div>
                        )}
                    </div>
                )}
                {boardView === 'sections' && (
                    <div className='tv-grid tv-grid--sections'>
                        {tables.map(renderSectionTile)}
                        {tables.length === 0 && (
                            <div className='tv-empty'>
                                <span className='tv-empty__title'>No tables on this floor</span>
                                <span>Pick another floor above.</span>
                            </div>
                        )}
                    </div>
                )}
                </div>
                )}
            </div>

            {/* Z-Report Mode Banner */}
            {isZReportMode && (
                <div className='zreport-banner'>
                    <div className='zreport-banner-content'>
                        <div className='zreport-warning-icon'>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 9V13M12 17H12.01M12 3L2 21H22L12 3Z" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className='zreport-banner-text'>
                            <span className='zreport-title'>Z-Report Blocked</span>
                            <span className='zreport-subtitle'>You must close {remainingOpenTables} active table{remainingOpenTables !== 1 ? 's' : ''}.</span>
                        </div>
                    </div>
                    <button className='zreport-return-btn' onClick={handleReturnToZReport}>
                        Return to Z-Report
                    </button>
                </div>
            )}

            {sheetOpen && (
                <ReservationSheet
                    reservation={sheetFor}
                    dateKey={resv.dateKey}
                    fields={resv.fields}
                    settings={resv.settings}
                    tables={tables}
                    prefill={sheetPrefill}
                    floorId={selectedFloorId}
                    prostayEnabled={resv.prostayEnabled}
                    onSaved={(saved) => {
                        resv.updateLocal(saved);
                        if (saved.date !== resv.dateKey) resv.setDateKey(saved.date);
                        setSheetOpen(false);
                        setSheetFor(null);
                        setSheetPrefill(null);
                    }}
                    onClose={() => { setSheetOpen(false); setSheetFor(null); setSheetPrefill(null); }}
                    onPickTables={beginAssign}
                    onSeat={handleSeat}
                />
            )}

            {waitSheetOpen && (
                <WaitlistSheet
                    entry={waitSheetFor}
                    suggestQuote={suggestQuote}
                    onJoin={resv.joinWaitlist}
                    onSave={resv.updateWaiting}
                    onClose={() => { setWaitSheetOpen(false); setWaitSheetFor(null); }}
                />
            )}

            {showError && <ErrorNotification headerTitle={titleError} content={contentError} />}

            {showSpinner && (
                <Spinner />
            )}

            {
                showConfirmTransfer &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height, position: 'fixed' }} onClick={() => setsShowConfirmTransfer(false)}></div>
                    <ConfirmOrderAction onConfirm={handleTransferConfirmation} mainTable={getMainTransferTable()} newTable={getNewTransferTable()} title='Transfer Product Confirmation' content='Are you sure you want to transfer this product to another table?' onClose={() => setsShowConfirmTransfer(false)} />
                </>
            }

            {
                showConfirmMerge &&
                <>
                    <div style={{ ...styles.overlay, width: windowSize.width, height: windowSize.height, position: 'fixed' }} onClick={() => setsShowConfirmMerge(false)}></div>
                    <ConfirmOrderAction onConfirm={handleConfirmMerge} mainTable={getMaineMergeTable()} newTable={getNewMergeTables()} title='Merge Table Confirmation' content='Are you sure you want to merge these tables together?' onClose={() => setsShowConfirmMerge(false)} />
                </>
            }

            {isZReportMode && (
                <div style={{ marginTop: -70 }}>
                    {isReceiptOpen && (
                        <ReceiptPopup
                            isOpen={isReceiptOpen}
                            orderProducts={orderProducts}
                            onClose={handleCloseReceipt}
                            openCashPayment={openCashPayment}
                            openCardPayment={openCardPayment}
                            onBookOnRoom={handleBookOnRoom}
                            activeOrder={activeOrder}
                            showProductId={false}
                        />
                    )}

                    {isCashPaymentOpen && (
                        <CashPaymentPopup
                            isOpen={isCashPaymentOpen}
                            onClose={() => {
                                setIsCashPaymentOpen(false);
                                setIsReceiptOpen(true);
                            }}
                            activeOrder={activeOrder}
                            onPaymentComplete={handlePaymentComplete}
                        />
                    )}

                    {isCardPaymentOpen && (
                        <CardPaymentPopup
                            isOpen={isCardPaymentOpen}
                            onClose={() => {
                                setIsCardPaymentOpen(false);
                                setIsReceiptOpen(true);
                            }}
                            activeOrder={activeOrder}
                            onPaymentComplete={handlePaymentComplete}
                        />
                    )}
                </div>
            )}

            {sheetOpen && (
                <ReservationSheet
                    reservation={sheetFor}
                    dateKey={resv.dateKey}
                    fields={resv.fields}
                    settings={resv.settings}
                    tables={tables}
                    prefill={sheetPrefill}
                    floorId={selectedFloorId}
                    prostayEnabled={resv.prostayEnabled}
                    onSaved={(saved) => {
                        resv.updateLocal(saved);
                        if (saved.date !== resv.dateKey) resv.setDateKey(saved.date);
                        setSheetOpen(false);
                        setSheetFor(null);
                        setSheetPrefill(null);
                    }}
                    onClose={() => { setSheetOpen(false); setSheetFor(null); setSheetPrefill(null); }}
                    onPickTables={beginAssign}
                    onSeat={handleSeat}
                />
            )}

            {waitSheetOpen && (
                <WaitlistSheet
                    entry={waitSheetFor}
                    suggestQuote={suggestQuote}
                    onJoin={resv.joinWaitlist}
                    onSave={resv.updateWaiting}
                    onClose={() => { setWaitSheetOpen(false); setWaitSheetFor(null); }}
                />
            )}
        </div>
    );
};

const styles = StyleSheet.create({
    overlay: {
        backgroundColor: 'rgba(51, 51, 51, 0.6)',
        top: 0,
        left: 0,
        zIndex: 9998,
    },
});

export default Tables;